/** Fixed, read-only NRCS point-query transport shared by browser proxy and Node providers.
 * Official contract: https://sdmdataaccess.sc.egov.usda.gov/webservicehelp.aspx
 * JSON requires REST POST; the ASMX RunQuery endpoint is SOAP/XML only.
 */
export const NRCS_SDA_REST_URL = 'https://sdmdataaccess.sc.egov.usda.gov/Tabular/post.rest';
const NRCS_LEGACY_PATH = '/Tabular/SDMTabularService.asmx/RunQuery';
const MAX_NRCS_RESPONSE_BYTES = 64 * 1024;

/**
 * The SSURGO query the provider sends. Returns the DOMINANT component
 * of the map unit that intersects the (lng, lat) point, with the
 * engineering-relevant fields.
 *
 *   - `SDA_Get_Mukey_from_intersection_with_WktWgs84` is the SDA helper
 *     that resolves a WKT POINT to its SSURGO mukey. It's part of the
 *     standard SDA SQL extensions documented at
 *     https://sdmdataaccess.sc.egov.usda.gov/QueryHelp.aspx.
 *   - `component.comppct_r DESC` picks the dominant component; the
 *     `TOP 1` clause means we only return the strongest signal per
 *     mukey. (Map units commonly have multiple components — taking the
 *     dominant one mirrors what a county soil scientist would cite.)
 *
 * Exported for tests so the SQL stays in one place and the test can
 * assert the proxied URL includes it verbatim.
 */
export function buildSsurgoQuery(lat: number, lng: number): string {
  // SDA expects "POINT(lng lat)" — longitude first, NAD83 / WGS84.
  // We use parameter substitution-by-string-template because SDA's
  // Query endpoint doesn't support bind parameters; the inputs are
  // numeric and already validated by `validateCoords` at the dispatcher
  // boundary, so concatenation is safe here.
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    throw new Error('NRCS SSURGO: invalid point coordinates');
  }
  const wkt = `POINT(${lng} ${lat})`;
  return [
    'SELECT TOP 1',
    'mu.mukey,',
    'mu.muname,',
    'co.drainagecl,',
    'mua.brockdepmin,',
    'mua.wtdepannmin,',
    'cht.texcl AS texcl',
    'FROM mapunit mu',
    'LEFT OUTER JOIN muaggatt mua ON mu.mukey = mua.mukey',
    'INNER JOIN component co ON mu.mukey = co.mukey',
    'LEFT OUTER JOIN chorizon ch ON co.cokey = ch.cokey AND ch.hzdept_r = 0',
    'LEFT OUTER JOIN chtexturegrp chtg ON ch.chkey = chtg.chkey AND chtg.rvindicator = \'Yes\'',
    'LEFT OUTER JOIN chtexture cht ON chtg.chtgkey = cht.chtgkey',
    `WHERE mu.mukey IN (SELECT * FROM SDA_Get_Mukey_from_intersection_with_WktWgs84('${wkt}'))`,
    'ORDER BY co.comppct_r DESC',
  ].join(' ');
}


export interface NrcsSdaRequest {
  url: typeof NRCS_SDA_REST_URL;
  query: string;
}

/** Only this provider's exact bounded SELECT may become a POST. */
export function prepareNrcsSdaRequest(url: string | URL): NrcsSdaRequest | null {
  const parsed = new URL(url);
  if (parsed.hostname !== 'sdmdataaccess.sc.egov.usda.gov'
    || !['/Tabular/post.rest', NRCS_LEGACY_PATH].includes(parsed.pathname)) return null;
  const query = parsed.searchParams.get('Query');
  const format = parsed.searchParams.get('Format');
  const point = query?.match(/POINT\((-?[0-9.]+(?:e[+-]?\d+)?) (-?[0-9.]+(?:e[+-]?\d+)?)\)/i);
  if (parsed.protocol !== 'https:' || parsed.port || parsed.username || parsed.password || parsed.hash
    || [...parsed.searchParams.keys()].length !== 2 || format !== 'JSON+COLUMNNAME'
    || !query || query.length > 2_000 || !point) {
    throw new Error('NRCS SSURGO: unsupported request');
  }
  const canonical = buildSsurgoQuery(Number(point[2]), Number(point[1]));
  if (query !== canonical) throw new Error('NRCS SSURGO: only the canonical point query is allowed');
  return { url: NRCS_SDA_REST_URL, query: canonical };
}

/** No caller-provided URL, body, headers, or redirect destination is forwarded. */
export async function fetchNrcsSdaRequest(request: NrcsSdaRequest, signal: AbortSignal): Promise<Response> {
  const upstream = await fetch(NRCS_SDA_REST_URL, {
    method: 'POST', redirect: 'manual', signal,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'User-Agent': 'Datacrate-SiteRAG/1.0' },
    body: JSON.stringify({ query: request.query, format: 'JSON+COLUMNNAME' }),
  });
  if (upstream.status >= 300 && upstream.status < 400) {
    await upstream.body?.cancel();
    throw new Error('NRCS SSURGO: upstream redirect rejected');
  }
  if (Number(upstream.headers.get('content-length')) > MAX_NRCS_RESPONSE_BYTES) {
    await upstream.body?.cancel();
    throw new Error('NRCS SSURGO: response too large');
  }
  // Read under the caller's timeout; a TOP 1 result cannot justify an
  // unbounded response or an indefinitely stalled body after headers arrive.
  const reader = upstream.body?.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  if (reader) for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    size += value.byteLength;
    if (size > MAX_NRCS_RESPONSE_BYTES) {
      await reader.cancel();
      throw new Error('NRCS SSURGO: response too large');
    }
    chunks.push(value);
  }
  const body = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) { body.set(chunk, offset); offset += chunk.byteLength; }
  return new Response(size ? body : null, { status: upstream.status, statusText: upstream.statusText, headers: upstream.headers });
}
